"use client";

import { generateHTML } from "@tiptap/html";

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Typography } from "@tiptap/extension-typography";

// --- Custom Nodes ---
import { HorizontalRule } from "@/app/components/tiptap-components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import { SmallText } from "@/app/components/tiptap-components/tiptap-node/paragraph-node/small-text-extension";
import { HintNode } from "@/app/components/tiptap-components/tiptap-node/hint-node/hint-node-extension";

// --- Styles ---
import "@/app/components/tiptap-components/tiptap-node/paragraph-node/paragraph-node.scss";
import "@/app/components/tiptap-components/tiptap-node/hint-node/hint-node.scss";
import "@/app/components/tiptap-components/tiptap-node/blockquote-node/blockquote-node.scss";
import "@/app/components/tiptap-components/tiptap-node/code-block-node/code-block-node.scss";
import "@/app/components/tiptap-components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "@/app/components/tiptap-components/tiptap-node/list-node/list-node.scss";
import "@/app/components/tiptap-components/tiptap-node/image-node/image-node.scss";
import "@/app/components/tiptap-components/tiptap-node/video-embed-node/video-embed-node.scss";
import "@/app/components/tiptap-components/tiptap-node/button-link-node/button-link-node.scss";
import "@/styles/tiptap-shared.css";

// --- Renderer Styles ---
import "@/app/components/tiptap-editor/simple/simple-editor-static-renderer.scss";
import "@/app/components/tiptap-components/tiptap-node/gallery-node/gallery-node.scss";

import { Node, mergeAttributes } from "@tiptap/core";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createRoot, type Root } from "react-dom/client";
import { useConsentStore } from "@/app/stores/consent-store";
import { VideoConsentPlaceholder } from "@/app/components/ui/video-consent-placeholder";
import {
  buildButtonLinkDataAttributes,
  extractButtonLinkAttributes,
  getButtonLinkClassNames,
} from "@/app/components/tiptap-components/tiptap-node/button-link-node/button-link-node-helpers";
import { normalizeButtonLinkHref } from "@/app/utils/helpers/backend/tiptap/button-link-url";
import { sanitizeTiptapContent } from "./sanitize-tiptap-content";
import {
  type GalleryImageT,
  GalleryLightbox,
} from "../../ui/image-gallery/gallery-lightbox";
import {
  DEFAULT_GALLERY_LAYOUT,
  isGalleryLayout,
  getGalleryLayoutData,
  type GalleryHeroLayoutItemT,
  type GalleryGridLayoutItemT,
  type GalleryOverlayLayoutT,
  type GalleryLayoutT,
} from "@/app/components/tiptap-components/tiptap-node/gallery-node/gallery-layout-helpers";
import type { JSONContent } from "@tiptap/core";
import type { DOMOutputSpec } from "@tiptap/pm/model";
import type { VideoAspectRatioT } from "@/app/components/tiptap-components/tiptap-node/video-embed-node/video-embed-node-extension";
import {
  normalizeTiptapContentAttributes,
  normalizeVideoAspectRatio,
} from "@/app/utils/helpers/backend/tiptap/content-attribute-security";
import { convertToEmbedUrl } from "@/app/utils/helpers/backend/tiptap/video-url-converter";
import { normalizeTiptapWhitespace } from "@/app/utils/helpers/backend/tiptap/normalize-tiptap-whitespace";

// Custom Gallery Node for static rendering only
const GalleryNodeStatic = Node.create({
  name: "gallery",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      images: {
        default: [],
      },
      layout: {
        default: DEFAULT_GALLERY_LAYOUT,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="gallery"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    let images: GalleryImageT[] = [];
    if (Array.isArray(HTMLAttributes.images)) {
      images = HTMLAttributes.images as GalleryImageT[];
    } else if (typeof HTMLAttributes["data-images"] === "string") {
      try {
        const parsed = JSON.parse(HTMLAttributes["data-images"] as string);
        if (Array.isArray(parsed)) {
          images = parsed;
        }
      } catch (error) {
        console.warn("Failed to parse gallery images", error);
      }
    }

    const rawLayout =
      (HTMLAttributes.layout as string | undefined) ??
      (HTMLAttributes["data-layout"] as string | undefined) ??
      DEFAULT_GALLERY_LAYOUT;
    const layoutValue: GalleryLayoutT = isGalleryLayout(rawLayout)
      ? rawLayout
      : DEFAULT_GALLERY_LAYOUT;
    const layoutData = getGalleryLayoutData(images, layoutValue);
    const serializedImages = JSON.stringify(images);

    if (layoutData.type === "empty") {
      return [
        "div",
        {
          class: "tiptap-gallery not-prose tiptap-gallery--empty",
          "data-type": "gallery",
        },
      ];
    }

    const baseAttributes: Record<string, string> = {
      class: "tiptap-gallery not-prose",
      "data-type": "gallery",
    };

    const createImageNode = (image: GalleryImageT): DOMOutputSpec => [
      "img",
      {
        src: image.src,
        alt: image.alt || "",
        title: image.title || "",
        class: "tiptap-gallery__image",
        loading: "lazy",
        decoding: "async",
      },
    ];

    if (layoutData.type === "hero") {
      const wrapperAttributes = {
        ...baseAttributes,
        class: `tiptap-gallery not-prose tiptap-gallery--hero${layoutData.isSingleImage ? " tiptap-gallery--hero-single" : ""}`,
        "data-images": serializedImages,
        "data-layout": layoutValue,
      };

      const items = layoutData.items.map(
        (item: GalleryHeroLayoutItemT, index) => {
          const itemClasses = [
            "tiptap-gallery__item",
            "tiptap-gallery__item--hero",
            item.itemType === "primary"
              ? "tiptap-gallery__item--hero-main"
              : "tiptap-gallery__item--hero-secondary",
          ];

          if (item.itemType === "primary" && layoutData.isSingleImage) {
            itemClasses.push("tiptap-gallery__item--hero-single");
          }

          const children: DOMOutputSpec[] = [createImageNode(item.image)];

          if (item.showRemainingOverlay && layoutData.remainingCount > 0) {
            const remainingBadge: DOMOutputSpec = [
              "div",
              { class: "tiptap-gallery__overlay-count" },
              `+${layoutData.remainingCount} weitere`,
            ];
            children.push(remainingBadge);
          }

          const galleryItem: DOMOutputSpec = [
            "div",
            {
              class: itemClasses.join(" "),
              "data-index": String(index),
              "data-image-src": item.image.src,
            },
            ...children,
          ];

          return galleryItem;
        }
      );

      return ["div", wrapperAttributes, ...items];
    }

    if (layoutData.type === "grid") {
      const wrapperAttributes = {
        ...baseAttributes,
        class: "tiptap-gallery not-prose tiptap-gallery--grid",
        "data-images": serializedImages,
        "data-layout": layoutValue,
      };

      const items = layoutData.items.map(
        (item: GalleryGridLayoutItemT, index: number) => {
          const children: DOMOutputSpec[] = [createImageNode(item.image)];

          if (item.showRemainingOverlay && layoutData.remainingCount > 0) {
            const remainingBadge: DOMOutputSpec = [
              "div",
              { class: "tiptap-gallery__overlay-count" },
              `+${layoutData.remainingCount} weitere`,
            ];
            children.push(remainingBadge);
          }

          const galleryItem: DOMOutputSpec = [
            "div",
            {
              class: "tiptap-gallery__item tiptap-gallery__item--grid",
              "data-index": String(index),
              "data-image-src": item.image.src,
            },
            ...children,
          ];

          return galleryItem;
        }
      );

      return ["div", wrapperAttributes, ...items];
    }

    const overlayData: GalleryOverlayLayoutT = layoutData;
    const wrapperAttributes = {
      ...baseAttributes,
      class: "tiptap-gallery not-prose tiptap-gallery--overlay",
      "data-images": serializedImages,
      "data-layout": layoutValue,
    };

    const thumbnailChildren: DOMOutputSpec[] = overlayData.thumbnails.map(
      (thumb, index) =>
        [
          "div",
          {
            class: "tiptap-gallery__overlay-thumbnail",
            "data-index": String(index + 1),
            "data-image-src": thumb.src,
          },
          createImageNode(thumb),
        ] as DOMOutputSpec
    );

    if (overlayData.remainingCount > 0) {
      const remainingBadge: DOMOutputSpec = [
        "div",
        {
          class:
            "tiptap-gallery__overlay-count tiptap-gallery__overlay-count--compact",
        },
        `+${overlayData.remainingCount}`,
      ];
      thumbnailChildren.push(remainingBadge);
    }

    const thumbnailsWrapper: DOMOutputSpec | null = thumbnailChildren.length
      ? [
          "div",
          { class: "tiptap-gallery__overlay-thumbnails" },
          ...thumbnailChildren,
        ]
      : null;

    const mainChild: DOMOutputSpec = [
      "div",
      {
        class: "tiptap-gallery__overlay-main",
        "data-index": "0",
        "data-image-src": overlayData.mainImage.src,
      },
      createImageNode(overlayData.mainImage),
      ...(thumbnailsWrapper ? [thumbnailsWrapper] : []),
    ];

    const overlayNode: DOMOutputSpec = ["div", wrapperAttributes, mainChild];

    return overlayNode;
  },
});

// Custom ButtonLink Node for static rendering only
const ButtonLinkNodeStatic = Node.create({
  name: "buttonLink",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      text: {
        default: "Button",
        parseHTML: (element) => element.getAttribute("data-text") || "Button",
        renderHTML: (attributes) => {
          return { "data-text": attributes.text || "Button" };
        },
      },
      href: {
        default: "#",
        parseHTML: (element) =>
          normalizeButtonLinkHref(element.getAttribute("href")) ?? "#",
        renderHTML: (attributes) => {
          return { href: normalizeButtonLinkHref(attributes.href) ?? "#" };
        },
      },
      variant: {
        default: "default",
        parseHTML: (element) =>
          element.getAttribute("data-variant") || "default",
        renderHTML: (attributes) => {
          return { "data-variant": attributes.variant || "default" };
        },
      },
      size: {
        default: "default",
        parseHTML: (element) => element.getAttribute("data-size") || "default",
        renderHTML: (attributes) => {
          return { "data-size": attributes.size || "default" };
        },
      },
      target: {
        default: "_self",
        parseHTML: (element) => element.getAttribute("target") || "_self",
        renderHTML: (attributes) => {
          const target = attributes.target || "_self";
          if (target === "_blank") {
            return {
              target: "_blank",
              rel: "noopener noreferrer",
            };
          }
          return {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="button-link"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const extracted = extractButtonLinkAttributes(HTMLAttributes);
    const className = getButtonLinkClassNames(extracted);
    const mergedAttributes = mergeAttributes(HTMLAttributes, {
      href: extracted.href,
      class: className,
      "data-type": "button-link",
      ...buildButtonLinkDataAttributes(extracted),
    });

    if (extracted.target === "_blank") {
      mergedAttributes.target = "_blank";
      if (!mergedAttributes.rel) {
        mergedAttributes.rel = "noopener noreferrer";
      }
    } else {
      delete mergedAttributes.target;
      delete mergedAttributes.rel;
    }

    return ["a", mergedAttributes, extracted.text];
  },
});

// Custom Video Embed Node for static rendering only (without React NodeView)
const VideoEmbedNodeStatic = Node.create({
  name: "videoEmbed",
  group: "block",
  draggable: true,
  atom: true,

  addOptions() {
    return {
      aspectRatio: "16:9" as VideoAspectRatioT,
    };
  },

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) =>
          convertToEmbedUrl(element.getAttribute("data-src") ?? "") ?? "",
        renderHTML: (attributes) => {
          const src = convertToEmbedUrl(attributes.src);
          return src ? { "data-src": src } : {};
        },
      },
      aspectRatio: {
        default: this.options.aspectRatio || "16:9",
        parseHTML: (element) =>
          normalizeVideoAspectRatio(element.getAttribute("data-aspect-ratio")),
        renderHTML: (attributes) => {
          return {
            "data-aspect-ratio": normalizeVideoAspectRatio(
              attributes.aspectRatio
            ),
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="video-embed"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const src = convertToEmbedUrl(HTMLAttributes["data-src"]) ?? "";
    const aspectRatio = normalizeVideoAspectRatio(
      HTMLAttributes["data-aspect-ratio"]
    );

    // The iframe is inserted after the stored consent state is available.

    const isPortrait = aspectRatio === "9:16";

    const styleBase = `aspect-ratio: ${aspectRatio.replace(":", " / ")}; position: relative; width: 100%; overflow: hidden; border-radius: 0.5rem; background-color: rgb(24 24 27);`;
    const styleWithMaxWidth = isPortrait
      ? `${styleBase} max-width: 400px; margin-left: auto; margin-right: auto;`
      : styleBase;

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "video-embed",
        "data-video-src": src,
        "data-video-aspect-ratio": aspectRatio,
        class: "tiptap-video-embed tiptap-video-embed--pending",
        style: styleWithMaxWidth,
      }),
    ];
  },
});

function getStaticRendererExtensions() {
  return [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3, 4, 5],
        HTMLAttributes: { class: "tiptap-block-heading" },
      },
      paragraph: {
        HTMLAttributes: { class: "tiptap-block-paragraph" },
      },
      horizontalRule: false,
    }),
    Typography,
    Image,
    TaskList,
    TaskItem.configure({ nested: true }),
    TextAlign.configure({ types: ["heading", "paragraph", "smallText"] }),
    Highlight.configure({ multicolor: true }),
    Subscript,
    Superscript,
    HorizontalRule,
    SmallText,
    HintNode,
    ButtonLinkNodeStatic,
    VideoEmbedNodeStatic.configure({
      aspectRatio: "16:9",
    }),
    GalleryNodeStatic,
  ];
}

export function generateStaticTiptapHtml(
  content: JSONContent | null
): string | null {
  if (!content) return "";

  try {
    const sanitizedContent = sanitizeTiptapContent(content);
    if (!sanitizedContent) return "";

    // NBSP from Word/Outlook pastes would otherwise turn a paragraph into
    // one unbreakable word (mid-word line breaks in the read view).
    const normalizedContent = normalizeTiptapContentAttributes(
      normalizeTiptapWhitespace(sanitizedContent)
    );
    return generateHTML(normalizedContent, getStaticRendererExtensions());
  } catch (error) {
    console.error("Error rendering content:", error);
    return null;
  }
}

export type SimpleEditorStaticRendererPropsT = {
  content: JSONContent | null;
  className?: string;
};

/**
 * Static renderer component for displaying Tiptap content
 * Uses generateHTML to convert JSON to HTML without an editor instance
 * Provides perfect read-only rendering without any editing capabilities
 */
export function SimpleEditorStaticRenderer({
  content,
  className = "",
}: SimpleEditorStaticRendererPropsT) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<GalleryImageT[]>([]);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const videoRootsRef = useRef<Map<HTMLElement, Root>>(new Map());

  // Get consent state from store
  const consent = useConsentStore((state) => state.consent);
  // null = not yet loaded, false = loaded but not consented, true = consented
  const consentLoaded = consent !== null;
  const hasMarketingConsent = consent?.preferences?.marketing ?? false;

  /**
   * Hydrate video embeds based on marketing consent status.
   * If marketing consent is given, render the actual iframe.
   * Otherwise, render a placeholder component.
   */
  const hydrateVideoEmbeds = useCallback(() => {
    const container = contentRef.current;
    if (!container) return;

    // Find all video embeds
    const videoContainers = container.querySelectorAll<HTMLElement>(
      ".tiptap-video-embed"
    );

    videoContainers.forEach((videoContainer) => {
      const src = convertToEmbedUrl(
        videoContainer.getAttribute("data-video-src") ?? ""
      );
      const aspectRatio = normalizeVideoAspectRatio(
        videoContainer.getAttribute("data-video-aspect-ratio")
      );

      // Check current state
      const isCurrentlyLoaded = videoContainer.classList.contains(
        "tiptap-video-embed--loaded"
      );
      const isCurrentlyBlocked = videoContainer.classList.contains(
        "tiptap-video-embed--blocked"
      );

      // Determine target state
      const shouldBeLoaded = hasMarketingConsent && src !== null;
      const shouldBeBlocked = !hasMarketingConsent || !src;

      // Skip if already in correct state
      if (shouldBeLoaded && isCurrentlyLoaded) return;
      if (shouldBeBlocked && isCurrentlyBlocked) return;

      // Cleanup existing React root before modifying
      const existingRoot = videoRootsRef.current.get(videoContainer);
      if (existingRoot) {
        try {
          existingRoot.unmount();
        } catch {
          // Ignore unmount errors
        }
        videoRootsRef.current.delete(videoContainer);
      }

      // Clear existing content and state classes
      videoContainer.innerHTML = "";
      videoContainer.classList.remove(
        "tiptap-video-embed--pending",
        "tiptap-video-embed--loaded",
        "tiptap-video-embed--blocked"
      );

      if (shouldBeLoaded) {
        // Render actual iframe
        const iframe = document.createElement("iframe");
        iframe.src = src;
        iframe.title = "Video Embed";
        iframe.className = "absolute inset-0 h-full w-full border-0 rounded-lg";
        iframe.allow =
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
        iframe.allowFullscreen = true;
        iframe.loading = "lazy";
        iframe.setAttribute(
          "sandbox",
          "allow-scripts allow-same-origin allow-presentation allow-popups"
        );
        iframe.setAttribute(
          "referrerpolicy",
          "strict-origin-when-cross-origin"
        );
        videoContainer.appendChild(iframe);
        videoContainer.classList.add("tiptap-video-embed--loaded");
      } else {
        // Render placeholder using React
        videoContainer.classList.add("tiptap-video-embed--blocked");

        const root = createRoot(videoContainer);
        videoRootsRef.current.set(videoContainer, root);

        root.render(
          <VideoConsentPlaceholder
            aspectRatio={aspectRatio.replace(":", " / ")}
          />
        );
      }
    });
  }, [hasMarketingConsent]);

  const htmlContent = useMemo(
    () => generateStaticTiptapHtml(content),
    [content]
  );

  // Cleanup React roots on unmount AND whenever the injected HTML changes:
  // dangerouslySetInnerHTML replaces the container elements, so roots created
  // for the previous markup would otherwise dangle until unmount.
  useEffect(() => {
    // Capture current ref value for cleanup
    const currentVideoRoots = videoRootsRef.current;

    return () => {
      // Copy roots to local variable to avoid race condition
      const rootsToUnmount = Array.from(currentVideoRoots.values());
      currentVideoRoots.clear();

      // Unmount asynchronously to avoid "unmount during render" error
      queueMicrotask(() => {
        rootsToUnmount.forEach((root) => {
          try {
            root.unmount();
          } catch {
            // Ignore errors from already unmounted roots
          }
        });
      });
    };
  }, [htmlContent]);

  // Hydrate video embeds when consent is loaded/changed
  useEffect(() => {
    if (!contentRef.current || htmlContent === null || !consentLoaded) return;

    // Use double requestAnimationFrame to ensure DOM is ready
    const frameIds = { id1: 0, id2: 0 };

    frameIds.id1 = requestAnimationFrame(() => {
      frameIds.id2 = requestAnimationFrame(() => {
        hydrateVideoEmbeds();
      });
    });

    return () => {
      cancelAnimationFrame(frameIds.id1);
      cancelAnimationFrame(frameIds.id2);
    };
  }, [htmlContent, consentLoaded, hasMarketingConsent, hydrateVideoEmbeds]);

  // Use event delegation instead of individual listeners
  useEffect(() => {
    const container = contentRef.current;
    if (!container || !htmlContent) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      const clickable = target.closest<HTMLElement>(
        ".tiptap-gallery__item, .tiptap-gallery__overlay-main, .tiptap-gallery__overlay-thumbnail"
      );

      if (!clickable) {
        return;
      }

      const gallery = clickable.closest<HTMLElement>(".tiptap-gallery");
      if (!gallery) {
        return;
      }

      const clickedImageElement =
        target instanceof HTMLImageElement
          ? target
          : clickable.querySelector("img");

      if (!(clickedImageElement instanceof HTMLImageElement)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const dataImagesAttr = gallery.getAttribute("data-images");
      const imageElements = Array.from(
        gallery.querySelectorAll<HTMLImageElement>("img")
      );

      const resolveSrc = (value: string) => {
        if (!value) {
          return "";
        }
        try {
          const origin =
            typeof window !== "undefined" && window.location
              ? window.location.origin
              : null;
          if (!origin) {
            return value; // Fallback to original value
          }
          return new URL(value, origin).href;
        } catch {
          return value;
        }
      };

      let images: GalleryImageT[] | null = null;
      if (dataImagesAttr) {
        try {
          const parsed = JSON.parse(dataImagesAttr);
          if (Array.isArray(parsed)) {
            images = parsed.map((item) => ({
              src: item.src,
              alt: item.alt || "",
              title: item.title || "",
            })) as GalleryImageT[];
          }
        } catch (error) {
          console.warn("Failed to parse gallery data-images attribute", error);
        }
      }

      let galleryImages: GalleryImageT[] = [];
      let initialIndex = 0;

      const clickedSrc =
        clickable.getAttribute("data-image-src") ||
        clickedImageElement.getAttribute("src") ||
        clickedImageElement.src;
      const normalizedClickedSrc = resolveSrc(clickedSrc);

      if (images && images.length > 0) {
        galleryImages = images;
        const foundIndex = images.findIndex(
          (img) => resolveSrc(img.src) === normalizedClickedSrc
        );
        if (foundIndex >= 0) {
          initialIndex = foundIndex;
        } else {
          const domIndex = imageElements.indexOf(clickedImageElement);
          if (domIndex >= 0) {
            initialIndex = domIndex;
          }
        }
      }

      if (galleryImages.length === 0) {
        galleryImages = imageElements.map((img) => ({
          src: img.src,
          alt: img.alt || "",
          title: img.title || "",
        }));
        initialIndex = Math.max(0, imageElements.indexOf(clickedImageElement));
      }

      setLightboxImages(galleryImages);
      setLightboxInitialIndex(initialIndex);
      setLightboxOpen(true);
    };

    // Add single delegated event listener to container
    container.addEventListener("click", handleClick);

    // Apply cursor style to all gallery items
    const interactiveNodes = container.querySelectorAll<HTMLElement>(
      ".tiptap-gallery__item, .tiptap-gallery__overlay-main, .tiptap-gallery__overlay-thumbnail"
    );
    interactiveNodes.forEach((node) => {
      node.style.cursor = "pointer";
    });

    // Cleanup
    return () => {
      container.removeEventListener("click", handleClick);
    };
  }, [htmlContent]);

  if (!content) {
    return (
      <div className="simple-editor-static-renderer-empty text-muted-foreground">
        <p>Noch kein Inhalt vorhanden.</p>
      </div>
    );
  }

  if (htmlContent === null) {
    return (
      <div className={`simple-editor-static-renderer ${className}`}>
        <div className="simple-editor-static-renderer-content prose prose-lg max-w-none">
          <p className="text-muted-foreground">
            Inhalt konnte nicht dargestellt werden.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`simple-editor-static-renderer ${className}`}>
        <div
          ref={contentRef}
          className="simple-editor-static-renderer-content prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>

      {/* Gallery Lightbox */}
      <GalleryLightbox
        images={lightboxImages}
        initialIndex={lightboxInitialIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
